import React, { useEffect, useMemo, useState, useRef } from 'react';
import { Box, Heading, Flex, Select, Button, HStack, Text, useToast } from '@chakra-ui/react';
import Page from '../../components/ui/Page';
import SectionCard from '../../components/ui/SectionCard';
import SearchInput from '../../components/filters/SearchInput';
import FilterSelect from '../../components/filters/FilterSelect';
import HelpTooltip from '../../components/ui/HelpTooltip';
import client from '../../utils/fetchClient';
import { useSearchParams } from 'react-router-dom';
import { useProjectContext } from '../../context/ProjectContext';
import CompareRunsPage from '../../pages/CompareRunsPage';
import { exportElementToPdf } from '../../utils/pdfUtils';

function RunSelect({ label, value, onChange, runs, search, onSearch, period, onPeriodChange }) {
  const filtered = useMemo(() => {
    if (!search) return runs;
    const needle = search.toLowerCase();
    return runs.filter(r => (r.name || r.id || '').toString().toLowerCase().includes(needle));
  }, [runs, search]);

  return (
    <Box flex={1} minW={0}>
      <Heading size="sm" mb={2}>{label}</Heading>
      <HStack spacing={3} mb={3} align="center" wrap="wrap">
        <SearchInput value={search} onChange={onSearch} onClear={() => onSearch('')} placeholder="Search runs..." maxW="260px" />
        <FilterSelect
          value={period}
          onChange={onPeriodChange}
          options={[
            { value: 'daily', label: 'Daily' },
            { value: 'weekly', label: 'Weekly' },
            { value: 'monthly', label: 'Monthly' },
            { value: 'quarterly', label: 'Quarterly' },
          ]}
          maxW="160px"
        />
      </HStack>
      <Select
        value={value || ''}
        onChange={e => onChange(e.target.value || undefined)}
        placeholder={`Select ${label}`}
        bg="white"
        variant="filled"
        borderRadius="md"
        w="100%"
      >
        {filtered.map((r, idx) => (
          <option key={(r.id || idx)} value={r.id}>{r.name || `Run #${r.id}`}</option>
        ))}
      </Select>
    </Box>
  );
}

export default function ComparePage() {
  const { effectiveProjectParam } = useProjectContext();
  const [searchParams, setSearchParams] = useSearchParams();
  
  // Ref for printable content
  const reportRef = useRef(null);
  const [contentReady, setContentReady] = useState(false);
  const [hideFiltersForPdf, setHideFiltersForPdf] = useState(false);
  const toast = useToast();

  // Independent state for A and B
  const [runsA, setRunsA] = useState([]);
  const [runsB, setRunsB] = useState([]);
  const [periodA, setPeriodA] = useState('weekly');
  const [periodB, setPeriodB] = useState('weekly');
  const [searchA, setSearchA] = useState('');
  const [searchB, setSearchB] = useState('');

  const runA = searchParams.get('runA') || '';
  const runB = searchParams.get('runB') || '';

  // Fetch list for Run A
  useEffect(() => {
    const params = new URLSearchParams();
    if (effectiveProjectParam) params.append('project', effectiveProjectParam);
    if (periodA) params.append('period', periodA);
    params.append('page_size', '50');
    client.get('/api/test-runs', { query: Object.fromEntries(params) })
      .then(res => res.json())
      .then(data => {
        const list = Array.isArray(data)
          ? data
          : Array.isArray(data?.runs)
          ? data.runs
          : Array.isArray(data?.items)
          ? data.items
          : Array.isArray(data?.results)
          ? data.results
          : Array.isArray(data?.data)
          ? data.data
          : [];
        setRunsA(list);
      })
      .catch(() => setRunsA([]));
  }, [effectiveProjectParam, periodA]);

  // Fetch list for Run B
  useEffect(() => {
    const params = new URLSearchParams();
    if (effectiveProjectParam) params.append('project', effectiveProjectParam);
    if (periodB) params.append('period', periodB);
    params.append('page_size', '50');
    client.get('/api/test-runs', { query: Object.fromEntries(params) })
      .then(res => res.json())
      .then(data => {
        const list = Array.isArray(data)
          ? data
          : Array.isArray(data?.runs)
          ? data.runs
          : Array.isArray(data?.items)
          ? data.items
          : Array.isArray(data?.results)
          ? data.results
          : Array.isArray(data?.data)
          ? data.data
          : [];
        setRunsB(list);
      })
      .catch(() => setRunsB([]));
  }, [effectiveProjectParam, periodB]);

  const updateParams = (next) => {
    const p = new URLSearchParams(searchParams);
    Object.entries(next).forEach(([k, v]) => {
      if (v === undefined || v === '') p.delete(k); else p.set(k, v);
    });
    setSearchParams(p, { replace: true });
  };

  return (
    <Page>
      <Flex mb={2} fontSize="sm" color="gray.600" gap={2} align="center">
        <Button variant="link" size="sm" onClick={() => window.history.back()}>Back</Button>
      </Flex>
      <Heading mb={4} fontWeight="bold" fontSize={{ base: '2xl', md: '3xl' }} color="gray.800" display="flex" alignItems="center">
        Compare <HelpTooltip label="Pick two runs to compare. Selection is saved in the URL for easy sharing." href="https://docs.example.com/qa/compare" />
      </Heading>
      {/* PDF export handled from child filter bar via callback */}

      <SectionCard mb={6}>
        <Flex gap={6} wrap={{ base: 'wrap', md: 'nowrap' }} align="flex-start">
          <RunSelect
            label="Run A"
            value={runA}
            onChange={(id) => updateParams({ runA: id })}
            runs={runsA}
            search={searchA}
            onSearch={setSearchA}
            period={periodA}
            onPeriodChange={setPeriodA}
          />
          <RunSelect
            label="Run B"
            value={runB}
            onChange={(id) => updateParams({ runB: id })}
            runs={runsB}
            search={searchB}
            onSearch={setSearchB}
            period={periodB}
            onPeriodChange={setPeriodB}
          />
          <HStack spacing={3} align="center" justify="flex-start" mt={{ base: 2, md: 6 }} minW={{ base: 'auto', md: '200px' }}>
            <Button size="sm" variant="outline" colorScheme="gray" onClick={() => updateParams({ runA: '', runB: '' })}>Clear</Button>
            <Button size="sm" colorScheme="green" onClick={() => updateParams({ runA: runB, runB: runA })} isDisabled={!runA || !runB}>Swap</Button>
          </HStack>
        </Flex>
      </SectionCard>

      <Box ref={reportRef} maxW="1024px">
        {(runA && runB) ? (
          <CompareRunsPage
            hideFilters={hideFiltersForPdf}
            onReady={setContentReady}
            onRequestExportPdf={async () => {
              if (!contentReady) {
                toast({ title: 'Preparing content…', status: 'info', duration: 1500 });
                return;
              }
              const node = reportRef.current;
              if (node) {
                setHideFiltersForPdf(true);
                await new Promise(r => setTimeout(r, 120));
                await exportElementToPdf(node, 'compare-report.pdf');
                setHideFiltersForPdf(false);
              }
            }}
          />
        ) : (
          <SectionCard>
            <Text color="gray.600">Select both Run A and Run B to view the comparison.</Text>
          </SectionCard>
        )}
      </Box>
    </Page>
  );
}
